const baseUrl = import.meta.env.VITE_DRIVE_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error("VITE_DRIVE_BFF_BASE_URL is required.");
}

export interface DriveItem {
    id: string;
    parentId: string | null;
    kind: "Folder" | "File";
    name: string;
    contentType: string | null;
    sizeBytes: number;
    createdAt: string;
    updatedAt: string;
    /** Soft-delete flag (Recycle Bin). */
    isTrashed?: boolean;
    trashedAt?: string | null;
    isFavorite?: boolean;
    /** Last download/preview time — drives the Recent view. */
    lastAccessedAt?: string | null;
    /** Set only on someone else's item shared with the user (absent = mine). */
    ownerEmail?: string;
    /** The user's access to a shared item. */
    role?: "viewer" | "editor";
    /** False when a viewer's download is blocked (preview still works). */
    canDownload?: boolean;
    /** Email of whoever saved the newest version (files only). */
    modifiedBy?: string;
    /** Shared with people or through a live link — always true for items shared with me. */
    isShared?: boolean;
}

export type TemplateApp = "docs" | "sheets" | "slides";

/** One entry of the template gallery (GET /api/templates). */
export interface DriveTemplate {
    id: string;
    name: string;
    app: TemplateApp;
    fileName: string;
    category: string;
    /** "#rrggbb" — drives the drawn thumbnail. */
    accent: string;
    /** blank · letter · invoice · resume · doc · report · grid · grid-chart · slide-dark · slide-light · slide-teal · slide-violet */
    thumb: string;
    description: string;
    blank: boolean;
    /** e.g. "Healthcare"; blanks and older catalogues are "General business". */
    industry: string;
}

/** The industry a template without one (older catalogue, blank files) falls under. */
export const GENERAL_INDUSTRY = "General business";

/** GET /api/templates, normalised: every template has an industry. */
export interface TemplateCatalogue {
    templates: DriveTemplate[];
    /** Non-blank industries in catalogue order (plus any a template names that the list missed). */
    industries: string[];
}

type RawTemplate = Omit<DriveTemplate, "industry"> & { industry?: string | null };

function normalizeTemplateCatalogue(r: { templates?: RawTemplate[]; industries?: unknown } | null | undefined): TemplateCatalogue {
    const templates = (r?.templates ?? []).map((t) => ({
        ...t,
        industry: typeof t.industry === "string" && t.industry.trim() ? t.industry.trim() : GENERAL_INDUSTRY
    }));
    const industries: string[] = [];
    const add = (s: unknown) => {
        const v = typeof s === "string" ? s.trim() : "";
        if (v && !industries.includes(v)) industries.push(v);
    };
    if (Array.isArray(r?.industries)) r.industries.forEach(add);
    templates.forEach((t) => add(t.industry));
    return { templates, industries };
}

export interface ShareLink {
    id: string;
    itemId: string;
    token: string;
    permission: "view" | "edit";
    expiresAt: string | null;
    createdAt: string;
    accessCount: number;
    hasPassword: boolean;
    /** Who may open the link. */
    audience: "anonymous" | "domain" | "email";
    /** Comma-separated recipient emails when audience = "email". */
    allowedEmails: string | null;
}

export interface FileVersion {
    id: string;
    versionNumber: number;
    sizeBytes: number;
    contentType: string | null;
    createdBy: string;
    createdAt: string;
}

export interface Quota {
    usedBytes: number;
    /** Effective limit: what you use plus what you can still add under your cap and the shared pool. */
    quotaBytes: number;
    availableBytes: number;
    /** FPT-10: the domain's shared pool behind quotaBytes (absent for a domain without a plan). */
    poolUsedBytes?: number | null;
    poolBytes?: number | null;
    seats?: number | null;
    /** A personal cap an administrator set, if any. */
    userCapBytes?: number | null;
}

/** One activity-log entry (GET /api/activity, GET /api/items/:id/activity). */
export interface DriveActivity {
    id: string;
    itemId: string;
    /** Name when it happened — kept after the item is deleted. */
    itemName: string;
    itemKind: "Folder" | "File";
    contentType?: string | null;
    /** "uploaded", "renamed", "moved", "shared", … (see activity/activityText.tsx). */
    action: string;
    actorEmail: string;
    ownerEmail: string;
    parentId?: string | null;
    /** Action-specific strings: from/to, version, emails + role, … */
    detail?: Record<string, string> | null;
    createdAt: string;
    /** "trashed" = in the caller's own bin; "deleted" = gone (or no longer reachable). */
    itemState: "live" | "trashed" | "deleted";
}

export interface ActivityPage {
    items: DriveActivity[];
    /** Pass as `before` for the next page; absent on the last page. */
    nextBefore?: string | null;
}

export type StorageCategoryKey =
    | "documents"
    | "spreadsheets"
    | "presentations"
    | "pdfs"
    | "images"
    | "videos"
    | "archives"
    | "other";

export interface StorageSummary {
    usedBytes: number;
    quotaBytes: number;
    availableBytes: number;
    fileCount: number;
    /** Current size of live files by type. */
    categories: { key: StorageCategoryKey; bytes: number; count: number }[];
    /** Old versions of live files (part of usedBytes). */
    versionBytes: number;
    versionCount: number;
    /** The bin (not part of usedBytes). */
    binBytes: number;
    binCount: number;
    /** FPT-10: the domain's shared pool behind quotaBytes (absent for a domain without a plan). */
    poolUsedBytes?: number | null;
    poolBytes?: number | null;
    seats?: number | null;
    /** A personal cap an administrator set, if any. */
    userCapBytes?: number | null;
}

export interface LargestFile {
    item: DriveItem;
    /** "My Drive / Projects / 2026". */
    location: string;
    versionCount: number;
    /** Bytes held by every version but the current one. */
    oldVersionBytes: number;
}

export interface LargestFilesPage {
    items: LargestFile[];
    total: number;
}

/** Flat folder reference returned by GET /api/folders for the move picker. */
export interface FolderRef {
    id: string;
    parentId: string | null;
    name: string;
}

/** Anonymous, 5-minute download link minted by POST /api/download-links. */
export interface DownloadLink {
    /** Absolute URL (resolved against the BFF base). */
    url: string;
    expiresAt: string;
}

/** A resumable (chunked) upload session — POST /api/uploads. */
export interface UploadSession {
    uploadId: string;
    /** Bytes per PUT the server asks for. */
    chunkSize: number;
    /** Bytes committed so far — where the next chunk starts. */
    offset: number;
    name: string;
    size: number;
    parentId: string | null;
    expiresAt: string;
}

/** "Request files" link on a folder (owner view). */
export interface FileRequest {
    id: string;
    folderId: string;
    token: string;
    title: string;
    instructions: string | null;
    expiresAt: string | null;
    isClosed: boolean;
    createdAt: string;
    uploadCount: number;
    lastUploadAt: string | null;
}

/** What an anonymous visitor of a file-request link sees. */
export interface PublicFileRequest {
    title: string;
    instructions: string | null;
    ownerEmail: string;
    expiresAt: string | null;
    maxFileBytes: number;
}

/** Who sent a file through a file request (typed by them — unverified). */
export interface UploadedBy {
    name: string;
    email: string;
    requestId: string;
    requestTitle: string;
    fileName: string;
    uploadedAt: string;
}

/** Error carrying the HTTP status + the server's ProblemDetails text. */
export class ApiError extends Error {
    constructor(message: string, readonly status: number) {
        super(message);
        this.name = "ApiError";
    }
}

/** RFC 7807 → one readable line: `detail ?? title`. */
function problemMessage(body: unknown, fallback: string): string {
    if (body && typeof body === "object") {
        const p = body as { title?: unknown; detail?: unknown };
        if (typeof p.detail === "string" && p.detail.trim()) return p.detail;
        if (typeof p.title === "string" && p.title.trim()) return p.title;
    }
    return fallback;
}

function friendlyStatus(status: number, statusText: string): string {
    if (status === 401) return "Your session has expired — please sign in again.";
    if (status === 403) return "You don't have permission to do that.";
    if (status === 404) return "That item no longer exists.";
    if (status === 413) return "The file is too large.";
    if (status >= 500) return "Drive had a problem completing that. Please try again.";
    return `Request failed (${status}${statusText ? ` ${statusText}` : ""})`;
}

async function fetchJson<T>(
    path: string,
    accessToken: string | undefined,
    init: RequestInit = {}
): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    let res: Response;
    try {
        res = await fetch(new URL(path, baseUrl).toString(), {
            ...init,
            headers,
            credentials: "omit"
        });
    } catch {
        throw new ApiError("Can't reach Drive — check your connection.", 0);
    }
    if (!res.ok) {
        let body: unknown = null;
        try { body = await res.json(); } catch { /* non-JSON response */ }
        throw new ApiError(problemMessage(body, friendlyStatus(res.status, res.statusText)), res.status);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

const enc = encodeURIComponent;

export const driveApi = {
    /** Absolute BFF URL for a server-relative path. */
    absoluteUrl: (path: string): string => new URL(path, baseUrl).toString(),

    /** Root→current folder chain for multi-level breadcrumbs. */
    ancestors: (id: string, token: string | undefined) =>
        fetchJson<FolderRef[]>(`/api/items/${enc(id)}/ancestors`, token),
    listItems: (parentId: string | null, token: string | undefined) => {
        const qs = parentId ? `?parentId=${enc(parentId)}` : "";
        return fetchJson<DriveItem[]>(`/api/items${qs}`, token);
    },
    search: (q: string, token: string | undefined, take = 25) => {
        const qs = `?q=${enc(q)}&take=${take}`;
        return fetchJson<DriveItem[]>(`/api/search${qs}`, token);
    },
    listAllFolders: (token: string | undefined) =>
        fetchJson<FolderRef[]>("/api/folders", token),
    getItem: (id: string, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/items/${enc(id)}`, token),
    createFolder: (parentId: string | null, name: string, token: string | undefined) =>
        fetchJson<DriveItem>("/api/folders", token, {
            method: "POST",
            body: JSON.stringify({ parentId, name })
        }),
    rename: (id: string, name: string, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/items/${enc(id)}`, token, {
            method: "PUT",
            body: JSON.stringify({ name })
        }),
    /** Permanent, recursive delete. */
    deleteItem: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/items/${enc(id)}`, token, { method: "DELETE" }),

    /**
     * Uploads a file, reporting byte progress (0..1) via onProgress. Uses
     * XMLHttpRequest rather than fetch because fetch has no upload-progress
     * event. Abort through `signal` (rejects with an AbortError).
     */
    uploadFile: (
        parentId: string | null,
        file: File,
        token: string | undefined,
        onProgress?: (fraction: number) => void,
        signal?: AbortSignal
    ): Promise<DriveItem> => new Promise<DriveItem>((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Upload cancelled", "AbortError"));
            return;
        }
        const form = new FormData();
        form.append("file", file);
        if (parentId) form.append("parentId", parentId);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", new URL("/api/files", baseUrl).toString());
        xhr.responseType = "json";
        xhr.setRequestHeader("Accept", "application/json");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        const onAbort = () => xhr.abort();
        signal?.addEventListener("abort", onAbort, { once: true });
        const cleanup = () => signal?.removeEventListener("abort", onAbort);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };
        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) {
                onProgress?.(1);
                resolve(xhr.response as DriveItem);
            } else {
                reject(new ApiError(problemMessage(xhr.response, friendlyStatus(xhr.status, xhr.statusText)), xhr.status));
            }
        };
        xhr.onerror = () => { cleanup(); reject(new ApiError("Upload failed — network error", 0)); };
        xhr.onabort = () => { cleanup(); reject(new DOMException("Upload cancelled", "AbortError")); };
        xhr.send(form);
    }),

    // ── Resumable (chunked) uploads ────────────────────────────────
    createUploadSession: (
        args: { name: string; size: number; contentType: string | null; parentId: string | null },
        token: string | undefined
    ) =>
        fetchJson<UploadSession>("/api/uploads", token, {
            method: "POST",
            body: JSON.stringify(args)
        }),
    /** Committed offset of a session (404 once it's completed, aborted or expired). */
    getUploadSession: (uploadId: string, token: string | undefined) =>
        fetchJson<UploadSession>(`/api/uploads/${enc(uploadId)}`, token),
    /**
     * PUTs bytes [start, start + chunk.size) with Content-Range. Resolves the
     * server's committed offset; rejects ApiError (409 = offset mismatch —
     * re-read the session) or an AbortError via `signal`.
     */
    uploadChunk: (
        uploadId: string,
        chunk: Blob,
        start: number,
        total: number,
        token: string | undefined,
        onProgress?: (loadedBytes: number) => void,
        signal?: AbortSignal
    ): Promise<{ offset: number }> => new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Upload cancelled", "AbortError"));
            return;
        }
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", new URL(`/api/uploads/${enc(uploadId)}`, baseUrl).toString());
        xhr.responseType = "json";
        xhr.setRequestHeader("Accept", "application/json");
        xhr.setRequestHeader("Content-Range", `bytes ${start}-${start + chunk.size - 1}/${total}`);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        const onAbort = () => xhr.abort();
        signal?.addEventListener("abort", onAbort, { once: true });
        const cleanup = () => signal?.removeEventListener("abort", onAbort);
        xhr.upload.onprogress = (e) => onProgress?.(e.loaded);
        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as { offset: number });
            else reject(new ApiError(problemMessage(xhr.response, friendlyStatus(xhr.status, xhr.statusText)), xhr.status));
        };
        xhr.onerror = () => { cleanup(); reject(new ApiError("Upload failed — network error", 0)); };
        xhr.onabort = () => { cleanup(); reject(new DOMException("Upload cancelled", "AbortError")); };
        xhr.send(chunk);
    }),
    /** Scans + stores the assembled file and creates the Drive item. */
    completeUpload: (uploadId: string, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/uploads/${enc(uploadId)}/complete`, token, { method: "POST" }),
    abortUpload: (uploadId: string, token: string | undefined) =>
        fetchJson<void>(`/api/uploads/${enc(uploadId)}`, token, { method: "DELETE" }),

    // ── File requests ──────────────────────────────────────────────
    listFileRequests: (folderId: string, token: string | undefined) =>
        fetchJson<FileRequest[]>(`/api/items/${enc(folderId)}/file-requests`, token),
    createFileRequest: (
        folderId: string,
        args: { title?: string; instructions?: string; expiresAt?: string | null },
        token: string | undefined
    ) =>
        fetchJson<FileRequest>(`/api/items/${enc(folderId)}/file-requests`, token, {
            method: "POST",
            body: JSON.stringify(args)
        }),
    setFileRequestClosed: (id: string, closed: boolean, token: string | undefined) =>
        fetchJson<FileRequest>(`/api/file-requests/${enc(id)}/${closed ? "close" : "reopen"}`, token, { method: "POST" }),
    deleteFileRequest: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/file-requests/${enc(id)}`, token, { method: "DELETE" }),
    /** Null when the file didn't come in through a file request. */
    uploadedBy: (fileId: string, token: string | undefined) =>
        fetchJson<UploadedBy | null>(`/api/files/${enc(fileId)}/uploaded-by`, token).then((r) => r ?? null),
    /** The page a file-request link opens (this web app, not the BFF). */
    publicFileRequestUrl: (token: string): string =>
        new URL(`/request/${enc(token)}`, window.location.origin).toString(),
    /** Anonymous: 200 = open, 404 = unknown, 410 = closed / expired. */
    resolveFileRequest: async (token: string): Promise<{ status: number; request: PublicFileRequest | null }> => {
        let res: Response;
        try {
            res = await fetch(new URL(`/api/file-requests/public/${enc(token)}`, baseUrl).toString(), {
                headers: { Accept: "application/json" },
                credentials: "omit"
            });
        } catch {
            return { status: 0, request: null };
        }
        return { status: res.status, request: res.ok ? ((await res.json()) as PublicFileRequest) : null };
    },
    /** Anonymous upload of one file into a file request (XHR for progress). */
    uploadToFileRequest: (
        token: string,
        file: File,
        uploader: { name: string; email: string },
        onProgress?: (fraction: number) => void,
        signal?: AbortSignal
    ): Promise<{ fileName: string; sizeBytes: number }> => new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Upload cancelled", "AbortError"));
            return;
        }
        const form = new FormData();
        form.append("name", uploader.name);
        form.append("email", uploader.email);
        form.append("file", file);
        const xhr = new XMLHttpRequest();
        xhr.open("POST", new URL(`/api/file-requests/public/${enc(token)}/files`, baseUrl).toString());
        xhr.responseType = "json";
        xhr.setRequestHeader("Accept", "application/json");
        const onAbort = () => xhr.abort();
        signal?.addEventListener("abort", onAbort, { once: true });
        const cleanup = () => signal?.removeEventListener("abort", onAbort);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };
        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as { fileName: string; sizeBytes: number });
            else reject(new ApiError(problemMessage(xhr.response, xhr.status === 410 ? "This request is closed." : friendlyStatus(xhr.status, xhr.statusText)), xhr.status));
        };
        xhr.onerror = () => { cleanup(); reject(new ApiError("Upload failed — network error", 0)); };
        xhr.onabort = () => { cleanup(); reject(new DOMException("Upload cancelled", "AbortError")); };
        xhr.send(form);
    }),

    /** Bearer-authenticated download URL (fetch with the Authorization header). */
    downloadUrl: (id: string): string =>
        new URL(`/api/files/${enc(id)}/download`, baseUrl).toString(),

    /** Fetches a file's bytes with the bearer header (preview paths). */
    fetchBlob: async (id: string, token: string | undefined, signal?: AbortSignal): Promise<Blob> => {
        const res = await fetch(new URL(`/api/files/${enc(id)}/download`, baseUrl).toString(), {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: "omit",
            signal
        });
        if (!res.ok) throw new ApiError(friendlyStatus(res.status, res.statusText), res.status);
        return res.blob();
    },

    /**
     * Mints an anonymous 5-minute link. One file streams as itself; a folder
     * or several items stream as a zip. Also usable as <img>/<video> src.
     */
    createDownloadLink: async (itemIds: string[], token: string | undefined): Promise<DownloadLink> => {
        const res = await fetchJson<{ url: string; expiresAt: string }>("/api/download-links", token, {
            method: "POST",
            body: JSON.stringify({ itemIds })
        });
        return { url: new URL(res.url, baseUrl).toString(), expiresAt: res.expiresAt };
    },

    /** URL of the 320 px JPEG thumbnail. 404s for non-image files; the
     *  caller should swap to a generic file icon on error. */
    thumbnailUrl: (id: string): string =>
        new URL(`/api/files/${enc(id)}/thumbnail`, baseUrl).toString(),

    /** Make a copy. No target = next to the original as "Copy of X". */
    copy: (id: string, target: { parentId?: string; toRoot?: boolean } | undefined, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/items/${enc(id)}/copy`, token, {
            method: "POST",
            body: JSON.stringify(target ?? {})
        }),

    zip: (itemIds: string[], name: string, parentId: string | null, token: string | undefined) =>
        fetchJson<DriveItem>("/api/zip", token, {
            method: "POST",
            body: JSON.stringify({ itemIds, name, parentId })
        }),
    unzip: (id: string, token: string | undefined) =>
        fetchJson<DriveItem[]>(`/api/files/${enc(id)}/unzip`, token, {
            method: "POST"
        }),
    protectPdf: (
        id: string,
        userPassword: string,
        ownerPassword: string,
        editLock: boolean,
        token: string | undefined
    ) =>
        fetchJson<DriveItem>(`/api/files/${enc(id)}/protect`, token, {
            method: "POST",
            body: JSON.stringify({ userPassword, ownerPassword, editLock })
        }),

    // Trash, favourites, recent, move and share
    trash: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/items/${enc(id)}/trash`, token, { method: "POST" }),
    restore: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/items/${enc(id)}/restore`, token, { method: "POST" }),
    listTrash: (token: string | undefined) =>
        fetchJson<DriveItem[]>("/api/trash", token),
    emptyTrash: (token: string | undefined) =>
        fetchJson<void>("/api/trash", token, { method: "DELETE" }),
    favorite: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/items/${enc(id)}/favorite`, token, { method: "POST" }),
    unfavorite: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/items/${enc(id)}/unfavorite`, token, { method: "POST" }),
    listFavorites: (token: string | undefined) =>
        fetchJson<DriveItem[]>("/api/favorites", token),
    listRecent: (take: number, token: string | undefined) =>
        fetchJson<DriveItem[]>(`/api/recent?take=${take}`, token),

    // Template gallery ("New" → Start something new)
    templates: (token: string | undefined) =>
        fetchJson<{ templates?: RawTemplate[]; industries?: unknown }>("/api/templates", token).then(normalizeTemplateCatalogue),
    /** Copies a template into the caller's Drive (unique name) and returns the new file. */
    copyTemplate: (id: string, args: { parentId: string | null; name?: string }, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/templates/${enc(id)}/copy`, token, {
            method: "POST",
            body: JSON.stringify(args)
        }),
    move: (id: string, newParentId: string | null, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/items/${enc(id)}/move`, token, {
            method: "POST",
            body: JSON.stringify({ newParentId })
        }),
    createShare: (
        id: string,
        opts: {
            permission?: "view" | "edit";
            audience?: "anonymous" | "domain" | "email";
            emails?: string[];
            expiresAt?: string | null;
            password?: string | null;
        },
        token: string | undefined
    ) =>
        fetchJson<ShareLink>(`/api/items/${enc(id)}/share`, token, {
            method: "POST",
            body: JSON.stringify(opts)
        }),
    listShares: (id: string, token: string | undefined) =>
        fetchJson<ShareLink[]>(`/api/items/${enc(id)}/shares`, token),
    revokeShare: (shareId: string, token: string | undefined) =>
        fetchJson<void>(`/api/shares/${enc(shareId)}`, token, { method: "DELETE" }),

    /** Builds the public share-link URL the user can copy + send. Points at
     *  the web app's own /shared/:token landing page (window.location.origin),
     *  NOT the BFF — the recipient opens a real page, not a JSON endpoint. */
    publicShareUrl: (token: string): string =>
        new URL(`/shared/${enc(token)}`, window.location.origin).toString(),

    /**
     * The universal link to an item for people who already have access —
     * Drive's /open/:id page finds where it lives for whoever opens it
     * (their folder, the owner's shared folder, or Shared with me). Mail
     * and Connect link to the same URL.
     */
    openLink: (id: string): string =>
        new URL(`/open/${enc(id)}`, window.location.origin).toString(),

    /**
     * Resolves a share token to its item metadata. Returns the HTTP status so
     * the landing page can distinguish: 200 = ok, 401 = restricted link, sign
     * in; 403 = signed in but not authorized; 404 = invalid/expired/wrong
     * password. `authToken` carries the signed-in user's bearer for domain/
     * email-restricted links; anonymous links ignore it.
     */
    resolveShare: async (
        shareToken: string,
        password: string | undefined,
        authToken: string | undefined
    ): Promise<{ status: number; item: DriveItem | null }> => {
        const qs = password ? `?password=${enc(password)}` : "";
        const headers = new Headers({ Accept: "application/json" });
        if (authToken) headers.set("Authorization", `Bearer ${authToken}`);
        const res = await fetch(new URL(`/api/shared/${enc(shareToken)}${qs}`, baseUrl).toString(), {
            headers, credentials: "omit"
        });
        const item = res.ok ? ((await res.json()) as DriveItem) : null;
        return { status: res.status, item };
    },

    /**
     * Downloads a shared file as a blob (so the bearer header can be sent for
     * restricted links — a plain navigation can't) and saves it under its name.
     */
    downloadShared: async (
        shareToken: string,
        fileName: string,
        password: string | undefined,
        authToken: string | undefined
    ): Promise<void> => {
        const qs = password ? `?password=${enc(password)}` : "";
        const headers = new Headers();
        if (authToken) headers.set("Authorization", `Bearer ${authToken}`);
        const res = await fetch(new URL(`/api/shared/${enc(shareToken)}/download${qs}`, baseUrl).toString(), {
            headers, credentials: "omit"
        });
        if (!res.ok) throw new Error(`Download failed → ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    },

    // Storage usage / quota
    quota: (token: string | undefined) => fetchJson<Quota>("/api/quota", token),
    storageSummary: (token: string | undefined) => fetchJson<StorageSummary>("/api/storage/summary", token),
    largestFiles: (skip: number, take: number, order: "asc" | "desc", token: string | undefined) =>
        fetchJson<LargestFilesPage>(`/api/storage/largest?skip=${skip}&take=${take}&order=${order}`, token),
    /** Owner only: deletes every version except the current one. */
    pruneVersions: (id: string, token: string | undefined) =>
        fetchJson<{ removedCount: number; freedBytes: number }>(`/api/files/${enc(id)}/versions/prune`, token, { method: "POST" }),

    // Activity log
    itemActivity: (id: string, token: string | undefined, take = 30, before?: string | null) =>
        fetchJson<ActivityPage>(`/api/items/${enc(id)}/activity?take=${take}${before ? `&before=${enc(before)}` : ""}`, token),
    activityFeed: (token: string | undefined, take = 40, before?: string | null) =>
        fetchJson<ActivityPage>(`/api/activity?take=${take}${before ? `&before=${enc(before)}` : ""}`, token),

    // Version history
    listVersions: (id: string, token: string | undefined) =>
        fetchJson<FileVersion[]>(`/api/files/${enc(id)}/versions`, token),
    /** Anonymous 5-minute link to one older version (owner or editor). */
    createVersionDownloadLink: async (id: string, versionId: string, token: string | undefined): Promise<DownloadLink> => {
        const res = await fetchJson<{ url: string; expiresAt: string }>(
            `/api/files/${enc(id)}/versions/${enc(versionId)}/download-link`,
            token,
            { method: "POST" }
        );
        return { url: new URL(res.url, baseUrl).toString(), expiresAt: res.expiresAt };
    },
    restoreVersion: (id: string, versionId: string, token: string | undefined) =>
        fetchJson<DriveItem>(`/api/files/${enc(id)}/versions/${enc(versionId)}/restore`, token, { method: "POST" }),
    uploadNewVersion: async (
        id: string,
        file: File,
        token: string | undefined
    ): Promise<DriveItem> => {
        const form = new FormData();
        form.append("file", file);
        const headers = new Headers();
        headers.set("Accept", "application/json");
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(
            new URL(`/api/files/${enc(id)}/versions`, baseUrl).toString(),
            { method: "POST", headers, body: form, credentials: "omit" }
        );
        if (!res.ok) {
            let body: unknown = null;
            try { body = await res.json(); } catch { /* non-JSON response */ }
            throw new ApiError(problemMessage(body, friendlyStatus(res.status, res.statusText)), res.status);
        }
        return (await res.json()) as DriveItem;
    }
};

/** Message for a caught error, suitable for a toast. */
export function errorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
}

export function isAbortError(err: unknown): boolean {
    return err instanceof DOMException && err.name === "AbortError";
}
